import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import type { ApPurchaseDocument } from "@/accounts-payable/types";
import { useApInvoiceDocument } from "@/accounts-payable/use-invoice-document";
import { documentLabel } from "@/accounts-payable/utils";
import { SectionHeading } from "./shared";

/** The document preview in a purchase document's detail panel. */
export const DocumentPreviewSection = ({
  document: purchaseDocument,
}: {
  document: ApPurchaseDocument;
}) => {
  const kind = purchaseDocument.document_type ?? "invoice";
  const label = documentLabel(purchaseDocument);
  const {
    objectUrl: documentUrl,
    isLoading: documentLoading,
    isError: documentError,
  } = useApInvoiceDocument(purchaseDocument.file_url);
  return (
    <Stack className="w-full" align="start" gap={4}>
      <Stack
        direction="row"
        justify="between"
        align="center"
        gap={4}
        className="w-full flex-wrap"
      >
        <SectionHeading>Document</SectionHeading>
        {documentUrl && (
          <a
            href={documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-link hover:underline"
          >
            Open in new tab
          </a>
        )}
      </Stack>
      {purchaseDocument.file_url ? (
        documentError ? (
          <Text tone="muted">Couldn’t load the {kind} document.</Text>
        ) : !documentUrl || documentLoading ? (
          <Text tone="muted">Loading document…</Text>
        ) : purchaseDocument.file_content_type?.startsWith("image/") ? (
          <img
            src={documentUrl}
            alt={label}
            className="w-full rounded-lg border border-line"
          />
        ) : (
          <iframe
            src={documentUrl}
            title={label}
            className="h-[460px] w-full rounded-lg border border-line"
          />
        )
      ) : (
        <Empty className={emptyStateVariants({ layout: "inline" })}>
          <EmptyHeader>
            <EmptyDescription>
              No document available for this {kind}.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </Stack>
  );
};
